<template>
  <div class="post__search">
    <h3 class="post__search-title">Найти экскурсию</h3>
    <div class="post__search-form">
      <ui-combobox-v3
        :key="selectedItem?.id"
        :items="resultList"
        :value="selectedItem || undefined"
        modal-title="Выберите город"
        placeholder="Выберите город"
        :modal-node="modalTarget"
        @selectedOption="selectedOptionHandler"
        @searchQuery="searchHandler"
      />
      <div
        class="date-input-wrapper"
        @click="inputClickHandler"
      >
        <ui-input
          type="text"
          :value="inputDates"
          icon-name="calendar_icon"
          icon-position="right"
          placeholder="Выберите даты"
          readonly
          @input="inputHandler"
        />
      </div>
      <AppLink
        :to="linkParams"
        target="_blank"
        @click.stop="buttonApplyHandler"
      >
        <ui-button
          :disabled="btnDisabledHandler"
          text="Показать"
        />
      </AppLink>
      <ui-modal
        :is-shown="active"
        class="calendar-component"
        :size="isMobile ? 'l' : 's'"
        is-header-fixed
        is-footer-fixed
        :modal-node="modalTarget"
        title="Даты"
        @close="close"
      >
        <template #title>Даты</template>
        <template #content>
          <div class="calendar">
            <Calendar
              :key="calendarShowingTrigger"
              :default-selected-dates="defaultDates"
              @select="inputCalendar"
            />
          </div>
        </template>
        <template #footer>
          <div class="calendar-footer">
            <div class="calendar-chips">
              <ui-chips-v3
                v-model="selectedChip"
                :options="chips"
                emit-value
                @update:model-value="changeChipsHandler"
              />
            </div>
            <div class="calendar-buttons">
              <ui-button
                text="Применить"
                @click.stop="confirmFilter"
              />
              <ui-button
                variant="ghost"
                text="Сбросить"
                @click.stop="clear"
              />
            </div>
          </div>
        </template>
      </ui-modal>
    </div>
    <div class="post-search-footer">
      <div class="post-search-footer__info">
        Более {{ formattedExperienceCount }}&nbsp;{{ pluralExperiences }} и&nbsp;туров по&nbsp;всему миру
      </div>
      <img
        alt="Авторские экскурсии на Tripster.ru"
        :src="tripsterLogoUrl"
        class="post-search-footer__logo"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, inject } from 'vue';
  import { addDays, differenceInCalendarDays, parseISO } from 'date-fns';
  import { UiInput, UiButton, UiComboboxV3, UiChipsV3 } from '@experience/vue3-kit';
  import { Calendar } from '@experience/core-calendar-ui';
  import { formatDate, calendarFormatMonths } from '@/shared/utils/time';
  import { choosePlural } from '@/shared/utils/language';
  import { useScreenSize } from '@/shared/utils/breakpoints';
  import { trackEvent } from '@/services/analytics';
  import { getGeoTypeahead } from '@/shared/api/experience';
  import { UiModal } from '@experience/core-modal-ui';

  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const modalTarget = shadowRoot?.querySelector('.modal-target') as HTMLElement;

  const tripsterLogoUrl = new URL('@/shared/ui/iconBase/svg/tripster-logo.svg', import.meta.url).href;

  const DATE_FORMAT = 'yyyy-MM-dd';

  // Tripster standard number formatting function
  const tripsterStandard = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return num.toString();
  };

  interface Chip {
    label: string;
    value: string | number;
    [key: string]: string | number | boolean | undefined;
  }

  interface SearchItem {
    id: string;
    title: string;
    selected: boolean;
    params: { slug: string };
  }

  interface Props {
    selected?: SearchItem;
    widgetLocation?: string;
    totalExperiencesCount: number;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'error', error: unknown): void;
  }>();

  const { isMobile } = useScreenSize();

  // Reactive state
  const resultList = ref<Array<{ id: string; title?: string; items: SearchItem[] }>>([]);
  const active = ref(false);
  const searchText = ref('');
  const defaultDates = ref<string[]>([]);
  const calendarValue = ref<string[]>([]);
  const inputDates = ref('');
  const slug = ref('');
  const start_date = ref<string | null>(null);
  const end_date = ref<string | null>(null);
  const selectedItem = ref<SearchItem | null>(null);
  const calendarShowingTrigger = ref(1);
  const selectedChip = ref<string | number>('weekend');
  const chips: Chip[] = [
    {
      label: 'Выходные',
      value: 'weekend',
    },
    {
      label: 'Завтра',
      value: 1,
    },
    {
      label: 'Послезавтра',
      value: 2,
    },
  ];

  // Methods
  const selectedOptionHandler = (item?: any) => {
    selectedItem.value = item;
    if (item.params.slug) {
      slug.value = item.params.slug;
    }
  };

  const btnDisabledHandler = computed(() => {
    return !searchText.value.length || !defaultDates.value.length;
  });

  const searchHandler = async (val: string) => {
    searchText.value = val;
    await fetchData(val);
  };

  const inputClickHandler = () => {
    active.value = true;

    if (calendarValue.value[0] && calendarValue.value[1]) {
      setChips(calendarValue.value[0], calendarValue.value[1]);
    }
  };

  const close = () => {
    active.value = false;
  };

  const inputHandler = (value: string) => {
    if (value === '') {
      reRenderCalendar();
    }
  };

  const inputCalendar = (from: string | null, to: string | null) => {
    if (from && to) {
      calendarValue.value = [from, to];
    } else if (from) {
      calendarValue.value = [from, from];
    }

    if (calendarValue.value[0] && calendarValue.value[1]) {
      setChips(calendarValue.value[0], calendarValue.value[1]);
      // Автоматически обновляем даты в инпуте при выборе в календаре
      // НЕ вызываем changeDates здесь, чтобы избежать бесконечного цикла
      stringDates(calendarValue.value[0], calendarValue.value[1]);
      setStartEndDates();
    }
  };

  const reRenderCalendar = () => {
    calendarShowingTrigger.value++;
  };

  const clear = () => {
    reRenderCalendar();
    active.value = false;
    defaultDates.value = [];
    calendarValue.value = [];
    inputDates.value = '';
    end_date.value = null;
    start_date.value = null;
    selectedChip.value = 'weekend';
  };

  const formatDates = (val: string | Date, start: string | Date, end: string | Date) => {
    const dateWithMonths = calendarFormatMonths(val, 'd MMMM');
    const dateWithoutMonth = formatDate(val, 'd');
    return isBothDatesInMonth(start, end) ? dateWithoutMonth : dateWithMonths;
  };

  const daysOfWeek = (start: string | Date, end: string | Date) => {
    const startStr = formatDate(start, 'EEEEEE');
    const endStr = formatDate(end, 'EEEEEE');
    return start === end ? startStr : `${startStr}–${endStr}`;
  };

  const isBothDatesInMonth = (start: string | Date, end: string | Date) => {
    return new Date(start).getMonth() === new Date(end).getMonth();
  };

  const stringDates = (start: string | Date, end: string | Date) => {
    const monthDate = calendarFormatMonths(start, 'MMMM');
    const daysOfWeekStr = daysOfWeek(start, end);
    const starts = formatDates(start, start, end);
    const ends = formatDates(end, start, end);
    if (start === end) {
      inputDates.value = `${ends} ${monthDate}, ${daysOfWeekStr}`;
    } else {
      inputDates.value = isBothDatesInMonth(start, end)
        ? `${starts}–${ends} ${monthDate}, ${daysOfWeekStr}`
        : `${starts} — ${ends}, ${daysOfWeekStr}`;
    }
  };

  const changeDates = (start: string | Date, end: string | Date) => {
    defaultDates.value = [formatDate(start, DATE_FORMAT), formatDate(end, DATE_FORMAT)];
    calendarValue.value = defaultDates.value;
  };

  const confirmFilter = () => {
    if (calendarValue.value[0] && calendarValue.value[1]) {
      changeDates(calendarValue.value[0], calendarValue.value[1]);
      setChips(calendarValue.value[0], calendarValue.value[1]);
      stringDates(calendarValue.value[0], calendarValue.value[1]);
      setStartEndDates();
    }
    active.value = false;
  };

  const setStartEndDates = () => {
    const [start, end] = calendarValue.value;
    if (start && end) {
      start_date.value = start;
      end_date.value = end;
    }
  };

  const changeChipsHandler = (val: any) => {
    if (val === 'weekend') {
      const { start, end } = nearestWeekendDate.value;
      if (start && end) {
        start_date.value = formatDate(start, DATE_FORMAT);
        end_date.value = formatDate(end, DATE_FORMAT);
        changeDates(start, end);
      }
    } else {
      const days = Number(val);
      const start = addDays(new Date(), days);
      start_date.value = formatDate(start, DATE_FORMAT);
      end_date.value = start_date.value;
      changeDates(start, start);
    }
  };

  const setChips = (startDate: string, endDate: string) => {
    const startDiff = differenceInCalendarDays(parseISO(startDate), new Date());
    const endDiff = differenceInCalendarDays(parseISO(endDate), new Date());
    const { start, end } = nearestWeekendDate.value;

    if (startDiff === 1 && endDiff === 1) {
      selectedChip.value = 1;
    } else if (startDiff === 2 && endDiff === 2) {
      selectedChip.value = 2;
    } else if (
      start &&
      end &&
      startDate === formatDate(start, DATE_FORMAT) &&
      endDate === formatDate(end, DATE_FORMAT)
    ) {
      selectedChip.value = 'weekend';
    } else {
      selectedChip.value = '';
    }
  };

  const setInitDates = () => {
    const { start, end } = nearestWeekendDate.value;
    if (start && end) {
      changeDates(start, end);
      stringDates(start, end);
    }
  };

  const fetchData = async (query = '') => {
    try {
      const { data } = await getGeoTypeahead({ query: { is_city_suggest: true, query } });

      if (data?.length) {
        const arr = data.map((el) => {
          return {
            id: el.asciiName,
            title: el.cityNameOnly || '',
            selected: false,
            params: { slug: el.asciiName || '' },
          };
        });
        resultList.value = [
          {
            id: '1',
            items: arr,
          },
        ];

        if (!query) {
          resultList.value[0].title = 'Популярные города';
        }
      } else {
        resultList.value = [];
      }
    } catch (error) {
      emit('error', error);
    }

    return resultList.value;
  };

  const sendSoguButtonClickHandler = async () => {
    // Send sogu analytics event
    try {
      await trackEvent('article-page:search-widget:button.click' as any, {
        widget_location: props.widgetLocation || '',
        city_slug: slug.value,
      });
    } catch {
      // Silently fail analytics
    }
  };

  const buttonApplyHandler = (event: Event) => {
    if (btnDisabledHandler.value) {
      event.preventDefault();
      return;
    }

    if (selectedItem.value?.params?.slug) {
      slug.value = selectedItem.value.params.slug;
    } else {
      slug.value = resultList.value[0]?.items[0]?.params?.slug || '';
    }

    sendSoguButtonClickHandler();
  };

  const linkParams = computed(() => {
    const query: Record<string, string> = {};

    if (end_date.value) {
      query.end_date = end_date.value;
    }

    if (start_date.value) {
      query.start_date = start_date.value;
    }

    return `/experience/${slug.value}/?${Object.entries(query)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`;
  });

  const nearestWeekendDate = computed(() => {
    const date = new Date();
    const weekendDates: {
      end: Date | null;
      start: Date | null;
    } = {
      end: null,
      start: null,
    };

    if (date.getDay() === 0) {
      weekendDates.start = addDays(new Date(), 6);
      weekendDates.end = addDays(new Date(), 7);
    } else if (date.getDay() === 6) {
      weekendDates.start = date;
      weekendDates.end = addDays(new Date(), 1);
    } else {
      const delta = 6 - date.getDay();
      weekendDates.start = addDays(new Date(), delta);
      weekendDates.end = addDays(new Date(), delta + 1);
    }
    return weekendDates;
  });

  const experienceCount = computed(() => {
    const count = String(props.totalExperiencesCount);

    if (count.length > 4) {
      return Number(count.replace(/[0-9]{3}$/, '000'));
    }

    if (count.length > 3) {
      return Number(count.replace(/[0-9]{2}$/, '00'));
    }

    if (count.length > 2) {
      return Number(count.replace(/[0-9]{1}$/, '0'));
    }

    return props.totalExperiencesCount;
  });

  const formattedExperienceCount = computed(() => tripsterStandard(experienceCount.value));

  const pluralExperiences = computed(() =>
    choosePlural(experienceCount.value, ['экскурсии', 'экскурсий', 'экскурсий']),
  );

  const fetchAndUseData = async () => {
    if (props.selected) {
      selectedOptionHandler(props.selected);
    }

    const searchSlug = selectedItem.value?.title ? selectedItem.value?.title : '';
    await fetchData(searchSlug);
  };

  // Lifecycle
  onMounted(async () => {
    fetchAndUseData();

    setInitDates();
    setStartEndDates();
  });
</script>

<style lang="scss" scoped>
  .post__search {
    display: flex;
    flex-direction: column;
    gap: var(--alias-space-16, 16px);
    align-self: stretch;
    box-sizing: border-box;
    max-width: 805px;
    margin: var(--alias-space-8, 8px) 0 var(--alias-space-48, 48px);
    padding: var(--alias-space-16, 16px);
    border-radius: var(--alias-radius-16, 16px);
    background: var(--alias-color-background-subtle);

    .post-description + & {
      margin: 48px 0;
    }

    @include tablet {
      margin: 0 0 var(--alias-space-32, 32px);
    }

    @include mobile {
      .post-description + & {
        margin: 40px 0;
      }
    }
  }

  .post__search-title {
    margin: 0;

    @include base-700-500-extra;

    @include tablet {
      @include base-600-500-extra;
    }
  }

  .post__search-form {
    display: grid;
    grid-template: 1fr / repeat(2, 1fr) auto;
    gap: 0 8px;
    margin: 0;

    :deep(.combobox),
    :deep(.combobox-input),
    :deep(.input-wrap) {
      width: 100%;
    }

    .date-input-wrapper {
      cursor: pointer;
    }

    @include mobile {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .button {
        margin-top: 8px;
      }
    }
  }

  .post-search-footer {
    display: flex;
    flex-wrap: wrap;
    gap: var(--alias-space-16) var(--alias-space-6);
    justify-content: space-between;
    padding-top: var(--alias-space-8);
  }

  .post-search-footer__info {
    flex-grow: 1;

    @include base-400-400-regular;

    @include mobile {
      @include base-400-300-regular;
    }
  }

  .post-search-footer__logo {
    flex-grow: 0;
    flex-shrink: 0;
    width: 94px;
    height: 20px;
    user-select: none;
  }

  .calendar-buttons {
    display: flex;
    gap: 8px;
    margin: 32px 0 0;

    .button {
      flex: 1;
    }
  }

  .calendar-chips {
    @include mobile {
      overflow-x: scroll;
      margin: 0 calc(var(--modal-padding) * -1);
      padding: 0 var(--modal-padding);

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .calendar-footer {
    display: flex;
    flex-direction: column;
  }
</style>
